import { Row } from '@tanstack/react-table';
import Badge from 'components/badge/Badge';
import {
  ConnectionConfigType,
  IntegrationStatus,
  InternalIntegrationSetting,
} from 'models/InternalIntegrationSetting';
import { INTERNAL_INTEGRATION_SETTINGS_BASE_QUERY_KEY } from 'pages/channels/integrationSettingsQuery';
import { useTranslation } from 'react-i18next';
import theme from 'styles/theme';
import NotAvailable from '../../../notAvailable/NotAvailable';
import DefaultSwitch from './DefaultSwitch/DefaultSwitch';
import InboundOAuthSwitch from './InboundOAuthSwitch';
import OutboundOAuthSwitch from './OutboundOAuthSwitch';

const StatusCell = ({ row }: { row: Row<InternalIntegrationSetting> }) => {
  const { t } = useTranslation();
  const { status } = row.original.agencySettings;
  const { connectionConfig } = row.original.integration;

  const integrationsQueryKey = [
    INTERNAL_INTEGRATION_SETTINGS_BASE_QUERY_KEY,
    'list',
  ];

  const isNotSupported = status === IntegrationStatus.NOT_SUPPORTED;
  const isPending = status === IntegrationStatus.PENDING;

  if (isNotSupported) {
    return <NotAvailable integrationId={row.original.integration.enumId} />;
  }

  // Default switch keeps the toggle visible and shows the pending badge below it.
  // Other connection types don't render a toggle when not active, so pending still hides them.
  if (
    isPending &&
    connectionConfig?.type !== ConnectionConfigType.SWITCH &&
    connectionConfig?.type !== ConnectionConfigType.LEGACY_V2_SWITCH
  ) {
    return (
      <Badge backgroundColor={theme.colors.hostfullyYellow}>
        {t('pageIntegrations.table.pending')}
      </Badge>
    );
  }

  switch (connectionConfig?.type) {
    // Connect Via Partner button
    case ConnectionConfigType.INBOUND_OAUTH:
      return (
        <InboundOAuthSwitch
          integrationSetting={row.original}
          queryKey={integrationsQueryKey}
        />
      );

    // Custom activation page
    case ConnectionConfigType.OUTBOUND_OAUTH:
      return (
        <OutboundOAuthSwitch
          integrationSetting={row.original}
          queryKey={integrationsQueryKey}
        />
      );

    // Default switch
    case ConnectionConfigType.LEGACY_V2_SWITCH:
    case ConnectionConfigType.SWITCH:
      return (
        <DefaultSwitch
          integrationSetting={row.original}
          queryKey={integrationsQueryKey}
          isPending={isPending}
        />
      );

    default:
      return null;
  }
};

export default StatusCell;
